import { Suspense, lazy, useEffect, type ReactNode } from "react";
import { Route, Routes, useLocation } from "react-router";
import { dropXScriptOnPrivatePage } from "../lib/adMeasurement";
import { TownLayout } from "./TownLayout";
import { AccountPage } from "./routes/AccountPage";
import { DemoBusinessPage } from "./routes/DemoBusinessPage";
import { DirectoryPage } from "./routes/DirectoryPage";
import { LegalPage } from "./routes/LegalPage";
import { NotFoundPage } from "./routes/NotFoundPage";
import { OrderPage } from "./routes/OrderPage";
import { OrdersPage } from "./routes/OrdersPage";
import { ResetPasswordPage } from "./routes/ResetPasswordPage";
import { SignUpDonePage } from "./routes/SignUpDonePage";
import { StatsPage } from "./routes/StatsPage";
import { TownView } from "./routes/TownView";
import { EarnPage } from "../ui/rewards/EarnCoins";
import { StepUpHost } from "../ui/staff/StaffAssurance";

// The advertiser, review and staff pages are separate chunks: the town loads without them.
const AdvertisePage = lazy(() => import("./routes/AdvertisePage").then((m) => ({ default: m.AdvertisePage })));
const ReviewPage = lazy(() => import("./routes/ReviewPage").then((m) => ({ default: m.ReviewPage })));
const AdminPage = lazy(() => import("./routes/AdminPage").then((m) => ({ default: m.AdminPage })));
const AdminAuditPage = lazy(() => import("./routes/AdminPage").then((m) => ({ default: m.AdminAuditPage })));
const AdminRolesPage = lazy(() => import("./routes/AdminPage").then((m) => ({ default: m.AdminRolesPage })));
// Finance staff pages (ADR-0040): one chunk for all of them.
const finance = () => import("./routes/AdminFinancePage");
const AdminFinanceCasesPage = lazy(() => finance().then((m) => ({ default: m.AdminFinanceCasesPage })));
const AdminFinanceCasePage = lazy(() => finance().then((m) => ({ default: m.AdminFinanceCasePage })));
const AdminFinanceRefundsPage = lazy(() => finance().then((m) => ({ default: m.AdminFinanceRefundsPage })));
const AdminFinanceAlertsPage = lazy(() => finance().then((m) => ({ default: m.AdminFinanceAlertsPage })));
const AdminFinanceOrdersPage = lazy(() => finance().then((m) => ({ default: m.AdminFinanceOrdersPage })));
const AdminFinanceOrderPage = lazy(() => finance().then((m) => ({ default: m.AdminFinanceOrderPage })));
const AdminFinanceLedgerPage = lazy(() => finance().then((m) => ({ default: m.AdminFinanceLedgerPage })));
const AdminFinanceReportPage = lazy(() => finance().then((m) => ({ default: m.AdminFinanceReportPage })));
const AdminFinanceOverviewPage = lazy(() => finance().then((m) => ({ default: m.AdminFinanceOverviewPage })));
const AdminPrivacyPage = lazy(() => import("./routes/AdminPage").then((m) => ({ default: m.AdminPrivacyPage })));

function Lazy({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <p className="muted" role="status" style={{ padding: 24 }}>
          Loading…
        </p>
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * Routes (brief section 5). `/` and `/place/:id` share one layout so the renderer and the
 * realtime membership persist while the selected property changes.
 */
export function App() {
  return (
    <>
      {/* ADR-0039: the "enter your code" dialog for staff confirmations, on every page. */}
      <StepUpHost />
      <XScriptGuard />
      <AppRoutes />
    </>
  );
}

/** ADR-0058: X's script never stays on a private page, however the visitor got there. */
function XScriptGuard() {
  const { pathname } = useLocation();
  useEffect(() => dropXScriptOnPrivatePage(pathname), [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<TownLayout />}>
        <Route index element={<TownView />} />
        <Route path="place/:id" element={<TownView />} />
      </Route>
      <Route path="directory" element={<DirectoryPage />} />
      <Route path="account" element={<AccountPage />} />
      <Route path="account/created" element={<SignUpDonePage />} />
      <Route path="orders" element={<OrdersPage />} />
      <Route path="orders/:id" element={<OrderPage />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="demo-business/:slug" element={<DemoBusinessPage />} />
      <Route path="stats" element={<StatsPage />} />
      <Route path="advertise" element={<Lazy><AdvertisePage /></Lazy>} />
      <Route path="review" element={<Lazy><ReviewPage /></Lazy>} />
      <Route path="admin" element={<Lazy><AdminPage /></Lazy>} />
      <Route path="admin/audit" element={<Lazy><AdminAuditPage /></Lazy>} />
      <Route path="admin/roles" element={<Lazy><AdminRolesPage /></Lazy>} />
      <Route path="admin/finance/cases" element={<Lazy><AdminFinanceCasesPage /></Lazy>} />
      <Route path="admin/finance/cases/:id" element={<Lazy><AdminFinanceCasePage /></Lazy>} />
      <Route path="admin/finance/refunds" element={<Lazy><AdminFinanceRefundsPage /></Lazy>} />
      <Route path="admin/finance/alerts" element={<Lazy><AdminFinanceAlertsPage /></Lazy>} />
      <Route path="admin/finance/orders" element={<Lazy><AdminFinanceOrdersPage /></Lazy>} />
      <Route path="admin/finance/orders/:id" element={<Lazy><AdminFinanceOrderPage /></Lazy>} />
      <Route path="admin/finance/ledger" element={<Lazy><AdminFinanceLedgerPage /></Lazy>} />
      <Route path="admin/finance/report" element={<Lazy><AdminFinanceReportPage /></Lazy>} />
      <Route path="admin/finance/overview" element={<Lazy><AdminFinanceOverviewPage /></Lazy>} />
      <Route path="admin/privacy" element={<Lazy><AdminPrivacyPage /></Lazy>} />
      <Route path="earn" element={<EarnPage />} />
      <Route path="legal" element={<LegalPage />} />
      <Route path="legal/:slug" element={<LegalPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
